import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { ErrorOutline as ErrorOutlineIcon, Close as CloseIcon } from '@mui/icons-material'
import { CSV_MAX_COLUMNS, CSV_MAX_ROWS, CSV_MAX_SIZE_MB } from '../../utils/campaignUtils'

export interface CsvFileClientErrorFlags {
  file_size?: boolean
  file_type?: boolean
  row_limit?: boolean
  column_limit?: boolean
  file_empty?: boolean
}

interface CsvFileClientErrorDialogProps {
  open: boolean
  onClose: () => void
  errors: CsvFileClientErrorFlags
}

const FILE_ERROR_MESSAGES: { key: keyof CsvFileClientErrorFlags; message: React.ReactNode }[] = [
  {
    key: 'file_size',
    message: (
      <>
        <strong>File size:</strong> File exceeds {CSV_MAX_SIZE_MB} MB. Upload a smaller file.
      </>
    ),
  },
  {
    key: 'file_type',
    message: (
      <>
        <strong>File type:</strong> File isn&apos;t a CSV. Upload a CSV file.
      </>
    ),
  },
  {
    key: 'file_empty',
    message: (
      <>
        <strong>Empty file:</strong> No data found. Upload a file with at least one row of data.
      </>
    ),
  },
  {
    key: 'row_limit',
    message: (
      <>
        <strong>Row limit:</strong> File exceeds {CSV_MAX_ROWS.toLocaleString()} rows. Upload a file with fewer rows.
      </>
    ),
  },
  {
    key: 'column_limit',
    message: (
      <>
        <strong>Column limit:</strong> File exceeds {CSV_MAX_COLUMNS} columns. Upload a file with fewer columns.
      </>
    ),
  },
]

const CsvFileClientErrorDialog: React.FC<CsvFileClientErrorDialogProps> = ({
  open,
  onClose,
  errors,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const activeErrors = FILE_ERROR_MESSAGES.filter(({ key }) => errors[key])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          ...(isMobile ? { m: 0, width: '95%', maxWidth: '100%', borderRadius: 2 } : {}),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 0.5, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="error" fontSize="small" />
          CSV file can&apos;t be uploaded
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 12,
            top: 10,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={isMobile ? { p: 2, pt: 1, pb: 3 } : { pb: 3 }}>
        <DialogContentText sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
          Your file doesn&apos;t meet the upload requirements. Fix the issue below and upload the file
          again.
        </DialogContentText>

        <Box
          component="ul"
          sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5 }}
        >
          {activeErrors.map(({ key, message }) => (
            <Box component="li" key={key}>
              <DialogContentText
                sx={{
                  color: 'text.primary',
                  fontSize: isMobile ? '0.875rem' : 'inherit',
                  wordBreak: 'break-word',
                }}
              >
                {message}
              </DialogContentText>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default CsvFileClientErrorDialog
